import React, { useEffect, useRef } from "react";
import { MwSmallIcon, Right, Wrong } from "../icons";
import type {
  ComparisonTableCell,
  ComparisonTableRow,
  VendorBrand,
} from "./comparison-types";
import "../styles/datadog-comparison-table.module.scss";

export type VendorComparisonTableProps = {
  className?: string;
  rows: ComparisonTableRow[];
  competitor: VendorBrand;
  tag?: string;
  title?: React.ReactNode;
  description?: string;
  ariaLabel?: string;
  sectionId?: string;
};

const StatusIcon: React.FC<{ status: "check" | "cross" }> = ({ status }) => (
  <span className="table-status-icon" aria-hidden="true">
    {status === "check" ? <Right /> : <Wrong />}
  </span>
);

const TableCellContent: React.FC<{ cell: ComparisonTableCell }> = ({ cell }) => {
  if (cell.text) {
    return <p className="table-cell-text">{cell.text}</p>;
  }

  const headlineClass =
    cell.headlineStyle === "gradient"
      ? "table-cell-headline text-gradient"
      : cell.headlineStyle === "bold"
        ? "table-cell-headline table-cell-headline--bold"
        : "table-cell-headline";

  return (
    <div className="table-cell-content">
      {cell.status ? <StatusIcon status={cell.status} /> : null}
      {cell.headline ? <p className={headlineClass}>{cell.headline}</p> : null}
      {cell.description ? (
        <p className="table-cell-description">{cell.description}</p>
      ) : null}
    </div>
  );
};

const ComparisonTableRowItem: React.FC<{
  row: ComparisonTableRow;
  index: number;
  competitor: VendorBrand;
}> = ({ row, index, competitor }) => (
  <div
    className={`comparison-table-row${
      index % 2 === 1 ? " comparison-table-row--alt" : ""
    }`}
  >
    <div className="comparison-table-label">
      <p className="comparison-table-label-title">{row.label}</p>
      {row.subtitle ? (
        <p className="comparison-table-label-subtitle">{row.subtitle}</p>
      ) : null}
    </div>
    <div className="comparison-table-cell comparison-table-cell--middleware">
      <div className="comparison-table-cell-brand" aria-hidden="true">
        <img
          src="/images/middleware-logo.svg"
          alt=""
          width={100}
          height={22}
        />
      </div>
      <TableCellContent cell={row.middleware} />
    </div>
    <div className="comparison-table-cell comparison-table-cell--competitor">
      <div className="comparison-table-cell-brand" aria-hidden="true">
        <img
          src={competitor.cellLogoSrc ?? competitor.logoSrc}
          alt=""
          width={competitor.cellLogoWidth ?? 72}
          height={competitor.cellLogoHeight ?? 22}
        />
      </div>
      <TableCellContent cell={row.competitor} />
    </div>
  </div>
);

const VendorComparisonTable: React.FC<VendorComparisonTableProps> = ({
  className = "",
  rows,
  competitor,
  tag = "Clear Comparison",
  title,
  description,
  ariaLabel,
  sectionId = "comparison",
}) => {
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    const header = stickyHeaderRef.current;
    if (!sentinel || !header) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        header.classList.toggle("is-stuck", entry.intersectionRatio < 1);
      },
      { threshold: [1] },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const resolvedTitle =
    title ?? (
      <>
        <span className="text-gradient">Feature-by-Feature Comparison</span>:
        Middleware vs {competitor.name}
      </>
    );

  const resolvedDescription =
    description ??
    `Evaluate on your own. Here's how Middleware stacks up against ${competitor.name}, capability by capability.`;

  return (
    <section
      className={`datadog-comparison-table vendor-comparison-table ${className}`.trim()}
      id={sectionId}
      aria-label={
        ariaLabel ??
        `Middleware vs ${competitor.name} feature comparison table`
      }
    >
      <div className="container">
        <div className="section-tag">
          <MwSmallIcon />
          {tag}
        </div>
        <h2>{resolvedTitle}</h2>
        <p className="text-center">{resolvedDescription}</p>
        <div className="comparison-table-scroll">
          <div
            className="comparison-table-sticky-sentinel"
            ref={stickySentinelRef}
            aria-hidden="true"
          />
          <div
            className="comparison-table-sticky-header"
            ref={stickyHeaderRef}
          >
            <div className="comparison-table-columns comparison-table-sticky-columns">
              <div className="comparison-table-header-label" />
              <div className="comparison-table-header-logo comparison-table-header-logo--middleware">
                <img
                  src="/images/middleware-logo.svg"
                  alt="Middleware"
                  width={182}
                  height={24}
                />
              </div>
              <div className="comparison-table-header-logo comparison-table-header-logo--competitor">
                <img
                  src={competitor.logoSrc}
                  alt={competitor.logoAlt ?? competitor.name}
                  width={competitor.headerLogoWidth ?? 119}
                  height={competitor.headerLogoHeight ?? 42}
                />
              </div>
            </div>
          </div>

          <div className="comparison-table-rows">
            {rows.map((row, index) => (
              <ComparisonTableRowItem
                row={row}
                index={index}
                competitor={competitor}
                key={row.id}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VendorComparisonTable;
